import React,{useEffect,useState}from"react";
import{Box,Collapse,Drawer,IconButton,List,ListItemButton,ListItemIcon,ListItemText,Menu,MenuItem,Tooltip,Typography,useMediaQuery}from"@mui/material";
import{useTheme}from"@mui/material/styles";
import{
  SettingsOutlined,
  Inventory2Outlined,
  PeopleOutline,
  ReceiptLongOutlined,
  DescriptionOutlined,
  PointOfSaleOutlined,
  PaymentsOutlined,
  AccountBalanceWalletOutlined,
  ExpandLessRounded,
  ExpandMoreRounded,
  CloseRounded
}from"@mui/icons-material";
import{useNavigate,useLocation}from"react-router-dom";
import{useDashboardTheme}from"../../../context/DashboardThemeContext";
import{useAuth}from"../../../context/AuthContext";
import logo from"../../../static/media/logo2.webp";

export const SIDEBAR_WIDTH=260;
export const SIDEBAR_COLLAPSED_WIDTH=76;
export const PANEL_HEADER_HEIGHT=76;

export default function Sidebar({open=false,onClose=()=>{}}){
  const muiTheme=useTheme();
  const isMobile=useMediaQuery(muiTheme.breakpoints.down("md"));
  const navigate=useNavigate();
  const location=useLocation();
  const{dashboardTheme}=useDashboardTheme();
  const{user}=useAuth();

  const[openMenus,setOpenMenus]=useState({
    inventory:false,
    treasury:false,
    payroll:false,
    configuration:false
  });

  const[menuAnchor,setMenuAnchor]=useState(null);
  const[activeMenuKey,setActiveMenuKey]=useState(null);

  const expanded=isMobile||open;
  const compact=!isMobile&&!open;

  const permissions=Array.isArray(user?.permissions)?user.permissions:[];
  const isAdmin=user?.role==="super_admin"||user?.role==="admin";

  const canViewEnterprise=isAdmin||permissions.includes("enterprise.view");
  const canViewProducts=isAdmin||permissions.includes("products.view");
  const canViewInventory=isAdmin||permissions.includes("inventory.view");
  const canViewPersons=isAdmin||permissions.includes("persons.view");
  const canViewVentas=isAdmin||permissions.includes("ventas.view");
  const canViewPurchases=isAdmin||permissions.includes("compras.view");
  const canViewTreasury=isAdmin||permissions.includes("tesoreria.view");
  const canViewReconciliation=isAdmin||permissions.includes("conciliacion.view");
  const canViewTreasuryMenu=canViewTreasury||canViewReconciliation;

  const path=location.pathname;
  const searchParams=new URLSearchParams(location.search);
  const currentSection=searchParams.get("section");

  const isProductsRoute=path.startsWith("/admin-panel/productos");
  const isInventoryMovementsRoute=path.startsWith("/admin-panel/inventario");
  const isInventoryRoute=isProductsRoute||isInventoryMovementsRoute;

  const isPeopleRoute=path.startsWith("/admin-panel/personas");
  const isOrdersRoute=path.startsWith("/admin-panel/ordenes");
  const isDocumentsRoute=path.startsWith("/admin-panel/documentos");
  const isCashRoute=path.startsWith("/admin-panel/cajas");

  const isPayrollRoute=path.startsWith("/admin-panel/nomina");
  const isPayrollOvertimeRoute=path.startsWith("/admin-panel/nomina/horas-extras");
  const isPayrollAdvancesRoute=path.startsWith("/admin-panel/nomina/anticipos");
  const isPayrollRolesRoute=isPayrollRoute&&!isPayrollOvertimeRoute&&!isPayrollAdvancesRoute;

  const isReconciliationRoute=path.startsWith("/admin-panel/tesoreria/conciliaciones");
  const isTreasuryRecordsRoute=path==="/admin-panel/tesoreria";
  const isTreasuryRoute=isTreasuryRecordsRoute||isReconciliationRoute;

  const isConfigRoute=path==="/admin-panel/configuracion";
  const isAppearanceActive=isConfigRoute&&(currentSection==="appearance"||!currentSection);
  const isAccessActive=isConfigRoute&&currentSection==="access";

  const background=dashboardTheme.sidebar_color;
  const foreground=dashboardTheme.sidebar_text_color;
  const border=dashboardTheme.border_color;
  const primary=dashboardTheme.primary_color;
  const isDark=dashboardTheme.theme_mode==="dark";

  const selectedBackground=primary;
  const selectedForeground="#FFFFFF";
  const hoverBackground=isDark?"rgba(255,255,255,0.08)":"rgba(127,127,127,0.08)";
  const activeSubmenuBackground=isDark?"rgba(255,255,255,0.12)":"rgba(127,127,127,0.12)";

  useEffect(()=>{
    setOpenMenus({
      inventory:isInventoryRoute,
      treasury:isTreasuryRoute,
      payroll:isPayrollRoute,
      configuration:isConfigRoute
    });
  },[isInventoryRoute,isTreasuryRoute,isPayrollRoute,isConfigRoute]);

  useEffect(()=>{
    setMenuAnchor(null);
    setActiveMenuKey(null);
  },[location.pathname,location.search,open,isMobile]);

  const closeCompactMenu=()=>{
    setMenuAnchor(null);
    setActiveMenuKey(null);
  };

  const navigateTo=destination=>{
    closeCompactMenu();

    if(path+location.search!==destination){
      navigate(destination);
    }

    if(isMobile){
      onClose();
    }
  };

  const toggleMenu=key=>{
    setOpenMenus(previous=>({
      inventory:key==="inventory"?!previous.inventory:false,
      treasury:key==="treasury"?!previous.treasury:false,
      payroll:key==="payroll"?!previous.payroll:false,
      configuration:key==="configuration"?!previous.configuration:false
    }));
  };

  const handleCompactMenu=(event,key)=>{
    event.preventDefault();
    event.stopPropagation();

    if(activeMenuKey===key&&Boolean(menuAnchor)){
      closeCompactMenu();
      return;
    }

    setMenuAnchor(event.currentTarget);
    setActiveMenuKey(key);
  };

  const menuItems=[
    {
      key:"inventory",
      label:"Inventario",
      short:"Invent.",
      icon:Inventory2Outlined,
      active:isInventoryRoute,
      children:[
        ...(canViewProducts?[{
          label:"Productos",
          path:"/admin-panel/productos",
          active:isProductsRoute
        }]:[]),
        ...(canViewInventory?[{
          label:"Movimientos",
          path:"/admin-panel/inventario",
          active:isInventoryMovementsRoute
        }]:[])
      ]
    },
    ...(canViewVentas?[
      {
        key:"orders",
        label:"Órdenes",
        short:"Órdenes",
        icon:ReceiptLongOutlined,
        path:"/admin-panel/ordenes",
        active:isOrdersRoute
      },
      {
        key:"documents",
        label:"Documentos",
        short:"Docs.",
        icon:DescriptionOutlined,
        path:"/admin-panel/documentos",
        active:isDocumentsRoute
      },
      {
        key:"cash",
        label:"Cajas",
        short:"Cajas",
        icon:PointOfSaleOutlined,
        path:"/admin-panel/cajas",
        active:isCashRoute
      }
    ]:[]),
    ...(canViewTreasuryMenu?[{
      key:"treasury",
      label:"Tesorería",
      short:"Tesorería",
      icon:AccountBalanceWalletOutlined,
      active:isTreasuryRoute,
      children:[
        ...(canViewTreasury?[{
          label:"Registros",
          path:"/admin-panel/tesoreria",
          active:isTreasuryRecordsRoute
        }]:[]),
        ...(canViewReconciliation?[{
          label:"Conciliaciones",
          path:"/admin-panel/tesoreria/conciliaciones",
          active:isReconciliationRoute
        }]:[])
      ]
    }]:[]),
    ...(canViewPurchases?[{
      key:"payroll",
      label:"Nómina",
      short:"Nómina",
      icon:PaymentsOutlined,
      active:isPayrollRoute,
      children:[
        {
          label:"Roles de pago",
          path:"/admin-panel/nomina/roles",
          active:isPayrollRolesRoute
        },
        {
          label:"Horas extras",
          path:"/admin-panel/nomina/horas-extras",
          active:isPayrollOvertimeRoute
        },
        {
          label:"Anticipos",
          path:"/admin-panel/nomina/anticipos",
          active:isPayrollAdvancesRoute
        }
      ]
    }]:[]),
    ...(canViewPersons?[{
      key:"persons",
      label:"Personas",
      short:"Personas",
      icon:PeopleOutline,
      path:"/admin-panel/personas",
      active:isPeopleRoute
    }]:[]),
    {
      key:"configuration",
      label:"Configuración",
      short:"Config.",
      icon:SettingsOutlined,
      active:isConfigRoute,
      children:[
        {
          label:"Apariencia",
          path:"/admin-panel/configuracion?section=appearance",
          active:isAppearanceActive
        },
        ...(canViewEnterprise?[{
          label:"Accesos",
          path:"/admin-panel/configuracion?section=access",
          active:isAccessActive
        }]:[])
      ]
    }
  ].filter(item=>!item.children||item.children.length>0);

  const activeCompactMenu=menuItems.find(item=>item.key===activeMenuKey);

  const compactMenuOpen=
    compact&&
    Boolean(menuAnchor)&&
    Boolean(activeCompactMenu?.children?.length);

  const renderSubmenu=items=>(
    <List disablePadding sx={{pb:0.5}}>
      {items.map(item=>(
        <ListItemButton
          key={item.path}
          selected={item.active}
          onClick={()=>navigateTo(item.path)}
          sx={{
            minHeight:37,
            mx:1.2,
            mb:0.35,
            pl:5.7,
            pr:1.5,
            py:0.7,
            borderRadius:2,
            color:foreground,
            bgcolor:item.active?activeSubmenuBackground:"transparent",
            "&.Mui-selected":{
              bgcolor:activeSubmenuBackground,
              color:foreground,
              "&:hover":{
                bgcolor:activeSubmenuBackground
              }
            },
            "&:hover":{
              bgcolor:hoverBackground
            }
          }}
        >
          <ListItemText
            primary={item.label}
            slotProps={{
              primary:{
                sx:{
                  fontSize:12.5,
                  fontWeight:item.active?600:400,
                  color:"inherit"
                }
              }
            }}
          />
        </ListItemButton>
      ))}
    </List>
  );

  const renderMenuItem=item=>{
    const Icon=item.icon;
    const hasChildren=Boolean(item.children?.length);
    const submenuOpen=Boolean(openMenus[item.key]);
    const compactActive=compact&&activeMenuKey===item.key&&compactMenuOpen;
    const selected=!hasChildren&&item.active;

    const handleClick=event=>{
      if(hasChildren){
        if(compact){
          handleCompactMenu(event,item.key);
          return;
        }

        closeCompactMenu();
        toggleMenu(item.key);
        return;
      }

      navigateTo(item.path);
    };

    const button=(
      <ListItemButton
        selected={selected}
        onClick={handleClick}
        aria-expanded={
          hasChildren
            ?compact
              ?compactActive
              :submenuOpen
            :undefined
        }
        aria-haspopup={hasChildren&&compact?"menu":undefined}
        sx={{
          minHeight:expanded?45:62,
          mx:expanded?1.2:0.75,
          mb:0.45,
          px:expanded?1.4:0.5,
          py:expanded?1:0.65,
          borderRadius:2,
          display:"flex",
          flexDirection:expanded?"row":"column",
          alignItems:"center",
          justifyContent:expanded?"flex-start":"center",
          gap:expanded?0:0.55,
          color:selected?selectedForeground:foreground,
          bgcolor:selected
            ?selectedBackground
            :compactActive
              ?hoverBackground
              :"transparent",
          "&.Mui-selected":{
            bgcolor:selectedBackground,
            color:selectedForeground,
            "&:hover":{
              bgcolor:selectedBackground
            }
          },
          "&:hover":{
            bgcolor:selected?selectedBackground:hoverBackground,
            color:selected?selectedForeground:foreground
          }
        }}
      >
        <ListItemIcon
          sx={{
            minWidth:expanded?36:0,
            width:expanded?"auto":"100%",
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            color:"inherit"
          }}
        >
          <Icon sx={{fontSize:21}}/>
        </ListItemIcon>

        {expanded?(
          <>
            <ListItemText
              primary={item.label}
              slotProps={{
                primary:{
                  sx:{
                    fontSize:13.5,
                    fontWeight:item.active?600:500,
                    color:"inherit",
                    whiteSpace:"nowrap",
                    overflow:"hidden",
                    textOverflow:"ellipsis"
                  }
                }
              }}
            />

            {hasChildren&&(
              submenuOpen?(
                <ExpandLessRounded sx={{fontSize:19,color:"inherit"}}/>
              ):(
                <ExpandMoreRounded sx={{fontSize:19,color:"inherit"}}/>
              )
            )}
          </>
        ):(
          <Typography
            sx={{
              fontSize:9,
              fontWeight:item.active?700:500,
              lineHeight:1.2,
              textAlign:"center",
              color:"inherit",
              width:"100%",
              overflowWrap:"anywhere"
            }}
          >
            {item.short}
          </Typography>
        )}
      </ListItemButton>
    );

    return(
      <Box key={item.key}>
        {compact?(
          <Tooltip
            title={item.label}
            placement="right"
            disableInteractive
            disableHoverListener={compactActive}
          >
            {button}
          </Tooltip>
        ):button}

        {expanded&&hasChildren&&(
          <Collapse
            in={submenuOpen}
            timeout="auto"
            unmountOnExit
          >
            {renderSubmenu(item.children)}
          </Collapse>
        )}
      </Box>
    );
  };

  const renderContent=(mobile=false)=>{
    const isCompact=!mobile&&!open;

    return(
      <Box
        sx={{
          width:"100%",
          height:"100%",
          minHeight:0,
          display:"flex",
          flexDirection:"column",
          bgcolor:background,
          color:foreground,
          overflow:"hidden"
        }}
      >
        <Box
          sx={{
            height:PANEL_HEADER_HEIGHT,
            minHeight:PANEL_HEADER_HEIGHT,
            flexShrink:0,
            width:"100%",
            boxSizing:"border-box",
            borderBottom:`1px solid ${border}`,
            position:"relative",
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            px:isCompact?0.5:1.5
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={()=>navigateTo("/admin-panel")}
            aria-label="Ir al panel de control"
            sx={{
              width:isCompact?54:170,
              maxWidth:"100%",
              height:60,
              p:0,
              m:0,
              border:0,
              background:"transparent",
              cursor:"pointer",
              display:"flex",
              alignItems:"center",
              justifyContent:"center",
              overflow:"visible"
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Yumim"
              sx={{
                display:"block",
                width:isCompact?48:155,
                maxWidth:"100%",
                height:isCompact?48:55,
                objectFit:"contain",
                objectPosition:"center",
                borderRadius:0,
                flexShrink:1
              }}
            />
          </Box>

          {mobile&&(
            <IconButton
              onClick={onClose}
              aria-label="Cerrar menú"
              sx={{
                position:"absolute",
                right:4,
                top:"50%",
                transform:"translateY(-50%)",
                width:30,
                height:30,
                color:foreground,
                "&:hover":{
                  bgcolor:hoverBackground
                }
              }}
            >
              <CloseRounded sx={{fontSize:19}}/>
            </IconButton>
          )}
        </Box>

        {!isCompact&&(
          <Typography
            sx={{
              px:2.5,
              pt:2.5,
              pb:1.2,
              fontSize:10,
              fontWeight:700,
              letterSpacing:"0.08em",
              color:foreground,
              opacity:0.7,
              flexShrink:0
            }}
          >
            MENÚ PRINCIPAL
          </Typography>
        )}

        <List
          disablePadding
          sx={{
            flex:"1 1 0",
            minHeight:0,
            overflowY:"auto",
            overflowX:"hidden",
            pt:isCompact?2:0,
            pb:2,
            scrollbarWidth:"thin",
            scrollbarColor:`${border} transparent`,
            "&::-webkit-scrollbar":{
              width:5
            },
            "&::-webkit-scrollbar-thumb":{
              backgroundColor:border,
              borderRadius:5
            }
          }}
        >
          {menuItems.map(renderMenuItem)}
        </List>
      </Box>
    );
  };

  return(
    <>
      <Box
        component="aside"
        sx={{
          display:{xs:"none",md:"block"},
          width:open?SIDEBAR_WIDTH:SIDEBAR_COLLAPSED_WIDTH,
          flexShrink:0,
          transition:"width 0.2s ease",
          boxSizing:"border-box"
        }}
      >
        <Box
          sx={{
            position:"fixed",
            top:0,
            left:0,
            bottom:0,
            width:open?SIDEBAR_WIDTH:SIDEBAR_COLLAPSED_WIDTH,
            height:"100dvh",
            zIndex:30,
            transition:"width 0.2s ease",
            borderRight:`1px solid ${border}`,
            boxSizing:"border-box",
            overflow:"hidden",
            bgcolor:background
          }}
        >
          {renderContent(false)}
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={compactMenuOpen}
        onClose={closeCompactMenu}
        disableScrollLock
        disableAutoFocusItem
        keepMounted
        anchorOrigin={{
          vertical:"top",
          horizontal:"right"
        }}
        transformOrigin={{
          vertical:"top",
          horizontal:"left"
        }}
        marginThreshold={8}
        slotProps={{
          paper:{
            sx:{
              ml:1,
              width:220,
              maxWidth:"calc(100vw - 100px)",
              maxHeight:"calc(100dvh - 16px)",
              borderRadius:"12px",
              border:`1px solid ${border}`,
              bgcolor:background,
              color:foreground,
              boxShadow:"0 8px 28px rgba(0,0,0,0.14)",
              overflowY:"auto"
            }
          },
          list:{
            sx:{
              py:0.6
            }
          }
        }}
      >
        <Box
          sx={{
            px:1.7,
            pt:1,
            pb:1.2,
            mb:0.5,
            borderBottom:`1px solid ${border}`
          }}
        >
          <Typography
            sx={{
              fontSize:12.5,
              fontWeight:700,
              color:foreground
            }}
          >
            {activeCompactMenu?.label||""}
          </Typography>
        </Box>

        {(activeCompactMenu?.children||[]).map(item=>(
          <MenuItem
            key={item.path}
            selected={item.active}
            onClick={()=>navigateTo(item.path)}
            sx={{
              minHeight:40,
              mx:0.7,
              my:0.2,
              px:1.4,
              py:0.8,
              borderRadius:"8px",
              fontSize:12.5,
              fontWeight:item.active?600:400,
              color:foreground,
              bgcolor:item.active?activeSubmenuBackground:"transparent",
              "&.Mui-selected":{
                bgcolor:activeSubmenuBackground,
                color:foreground,
                "&:hover":{
                  bgcolor:activeSubmenuBackground
                }
              },
              "&:hover":{
                bgcolor:hoverBackground
              }
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      <Drawer
        variant="temporary"
        open={isMobile&&open}
        onClose={onClose}
        ModalProps={{
          keepMounted:true,
          disableScrollLock:true
        }}
        sx={{
          display:{xs:"block",md:"none"},
          "& .MuiDrawer-paper":{
            width:SIDEBAR_WIDTH,
            maxWidth:"85vw",
            height:"100dvh",
            borderRadius:0,
            borderRight:`1px solid ${border}`,
            boxShadow:"none",
            bgcolor:background,
            overflow:"hidden"
          }
        }}
      >
        {renderContent(true)}
      </Drawer>
    </>
  );
}